import React, { useState, useRef, useEffect } from "react";
import Markdown from "react-markdown";
import { Button } from "./Button";
import { aiService } from "../services/aiService";
import type { ContentBlock } from "../services/aiService";
import type { AIEdit } from "../types/edit";
import { numberLines, stripGutter } from "../lib/edits";
import { assetService } from "../services/assetService";
import { useEscapeKey } from "../hooks/useEscapeKey";

interface BottomChatProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    markdown: string;
    onAddEdits?: (edits: AIEdit[]) => void;
    blogId?: string;
    onAssetSaved?: () => void;
}

interface Message {
    id: string;
    role: "user" | "ai";
    text?: string;
    image?: { data: string; mimeType: string };
}

export function BottomChat({ isOpen, setIsOpen, markdown, onAddEdits, blogId, onAssetSaved }: BottomChatProps) {
    const [height, setHeight] = useState(300); // Default height
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const [agentMode, setAgentMode] = useState<"editor" | "photographer">("editor");
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);

    // Save-image-as-asset modal state
    const [pendingImage, setPendingImage] = useState<{ data: string; mimeType: string } | null>(null);
    const [imageName, setImageName] = useState("");
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");

    useEffect(() => {
        if (isOpen && !conversationId) {
            const create = agentMode === "photographer"
                ? aiService.createPhotographerConversation()
                : aiService.createEditorConversation();
            create
                .then(res => setConversationId(res.id))
                .catch(err => console.error("Failed to create conversation:", err));
        }
    }, [isOpen, conversationId, agentMode]);

    useEscapeKey(pendingImage !== null && saveStatus !== "saving", () => {
        setPendingImage(null);
        setSaveStatus("idle");
    });

    useEffect(() => {
        // Scroll the message list itself; scrollIntoView would also scroll the
        // surrounding editor panes.
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [messages]);

    // Focus the composer when the chat opens (once the input is enabled) and
    // again when an AI reply finishes — same pattern as most chat UIs. Skip
    // while the save-asset modal owns focus.
    useEffect(() => {
        if (!isOpen || isLoading || !conversationId || pendingImage) return;
        inputRef.current?.focus();
    }, [isOpen, isLoading, conversationId, pendingImage]);

    const handleReset = async (nextMode?: "editor" | "photographer") => {
        if (conversationId) {
            try {
                await aiService.deleteConversation(conversationId);
            } catch (err) {
                console.error("Failed to delete conversation:", err);
            }
        }
        setConversationId(null);
        setMessages([]);
        setInputValue("");
        setHasSentFirstMessage(false);
        setIsLoading(false);
        if (nextMode) setAgentMode(nextMode);
    };

    const handleSwitchMode = (mode: "editor" | "photographer") => {
        if (mode === agentMode || isLoading) return;
        handleReset(mode);
    };

    const handleSaveImage = async () => {
        if (!pendingImage || !blogId || !imageName.trim() || saveStatus === "saving") return;
        setSaveStatus("saving");
        try {
            const { data, mimeType } = pendingImage;
            const byteChars = atob(data);
            const byteArr = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
            const ext = mimeType.split("/")[1] || "png";
            const filename = imageName.trim().endsWith("." + ext)
                ? imageName.trim()
                : `${imageName.trim()}.${ext}`;
            const file = new File([byteArr], filename, { type: mimeType });
            await assetService.upload("blog", blogId, file);
            setSaveStatus("success");
            onAssetSaved?.();
            setTimeout(() => {
                setPendingImage(null);
                setImageName("");
                setSaveStatus("idle");
            }, 1200);
        } catch (err) {
            console.error("Failed to save asset:", err);
            setSaveStatus("error");
        }
    };

    const handleSend = async () => {
        if (!conversationId || !inputValue.trim() || isLoading) return;

        const userText = inputValue.trim();
        const newMessage: Message = { id: Date.now().toString(), role: "user", text: userText };
        setMessages(prev => [...prev, newMessage]);
        setInputValue("");
        setIsLoading(true);

        try {
            const contentBlocks: ContentBlock[] = [];

            if (!hasSentFirstMessage) {
                // Include blog content as a hidden content block on the first request.
                // Lines are numbered so `insert_lines` has real line numbers to target.
                contentBlocks.push({
                    text: `Here is the current content of the blog, one line per numbered gutter entry:\n\n${numberLines(markdown)}\n\n---\n\nUser request:`
                });
            }

            contentBlocks.push({ text: userText });

            const response = await aiService.sendContent(conversationId, contentBlocks);

            if (!hasSentFirstMessage) {
                setHasSentFirstMessage(true);
            }

            const newMessages: Message[] = [];
            let currentText = "";

            const flushText = () => {
                if (currentText.trim()) {
                    newMessages.push({
                        id: Date.now().toString() + "-ai-text-" + Math.random().toString(36).substring(7),
                        role: "ai",
                        text: currentText
                    });
                    currentText = "";
                }
            };

            for (const part of response.parts) {
                if (part.text) {
                    currentText += (currentText ? "\n\n" : "") + part.text;
                } else {
                    flushText();

                    if ((part as any).inlineData) {
                        const { data, mimeType } = (part as any).inlineData;
                        newMessages.push({
                            id: Date.now().toString() + "-img-" + Math.random().toString(36).substring(7),
                            role: "ai",
                            image: { data, mimeType }
                        });
                    }

                    if (part.functionCall) {
                        let actionMsg = "";
                        if (part.functionCall.name === "edit_text" && onAddEdits) {
                            const args = part.functionCall.args;
                            const replacements = args && Array.isArray(args.replacements) ? args.replacements : [];
                            const newEdits: AIEdit[] = replacements.map((r: any, idx: number) => ({
                                id: Date.now().toString() + "-edit-" + Math.random().toString(36).substring(7),
                                type: "replace",
                                original: stripGutter(r.old_text),
                                new: stripGutter(r.new_text)
                            }));

                            if (newEdits.length > 0) {
                                onAddEdits(newEdits);
                                actionMsg = "_Suggested edits applied to the preview pane._";
                            }
                        } else if (part.functionCall.name === "insert_lines" && onAddEdits) {
                            const args = part.functionCall.args;
                            if (args && typeof args.line_number === "number" && Array.isArray(args.lines)) {
                                const newEdits: AIEdit[] = [{
                                    id: Date.now().toString() + "-insert-" + Math.random().toString(36).substring(7),
                                    type: "insert",
                                    line: args.line_number,
                                    new: stripGutter(args.lines.join("\n"))
                                }];
                                onAddEdits(newEdits);
                                actionMsg = "_Suggested insertions applied to the preview pane._";
                            }
                        }

                        if (actionMsg) {
                            newMessages.push({
                                id: Date.now().toString() + "-ai-tool-" + Math.random().toString(36).substring(7),
                                role: "ai",
                                text: actionMsg
                            });
                        }
                    }
                }
            }

            flushText();

            if (newMessages.length > 0) {
                setMessages(prev => [...prev, ...newMessages]);
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            setMessages(prev => [...prev, { id: Date.now().toString() + "-err", role: "ai", text: "Sorry, I encountered an error computing that request." }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Grow the composer with the draft, then scroll once it hits max height.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 144)}px`;
    }, [inputValue]);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        e.preventDefault(); // Prevent text selection while dragging
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging || !containerRef.current) return;

            // Calculate new height based on mouse Y position difference from bottom of window
            const newHeight = window.innerHeight - e.clientY;

            // Apply min and max constraints
            const clampedHeight = Math.max(200, Math.min(newHeight, window.innerHeight * 0.8));
            setHeight(clampedHeight);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging]);

    if (!isOpen) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="border-t border-base-300 bg-base-100 flex flex-col relative transition-shadow duration-200"
            style={{ height: `${height}px` }}
        >
            {/* Drag Handle (Top Border) */}
            <div
                className={`absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-primary/50 z-10 -translate-y-1/2 transition-colors ${isDragging ? 'bg-primary' : 'bg-transparent'}`}
                onMouseDown={handleMouseDown}
            />

            {/* Header */}
            <div className={`bg-base-200/80 backdrop-blur p-2 flex justify-between items-center border-b ${isDragging ? 'border-primary' : 'border-base-300'} transition-colors`}>
                <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                    </svg>
                    {/* Mode toggle */}
                    <div className="flex items-center bg-base-300 rounded-full p-0.5 gap-0.5 text-xs font-semibold">
                        <button
                            className={`px-3 py-0.5 rounded-full transition-colors ${agentMode === "editor"
                                    ? "bg-primary text-primary-content shadow-sm"
                                    : "text-base-content/60 hover:text-base-content"
                                }`}
                            onClick={() => handleSwitchMode("editor")}
                            disabled={isLoading}
                        >
                            Editor
                        </button>
                        <button
                            className={`px-3 py-0.5 rounded-full transition-colors ${agentMode === "photographer"
                                    ? "bg-primary text-primary-content shadow-sm"
                                    : "text-base-content/60 hover:text-base-content"
                                }`}
                            onClick={() => handleSwitchMode("photographer")}
                            disabled={isLoading}
                        >
                            Photographer
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        className="btn-ghost btn-xs btn-circle"
                        onClick={() => handleReset()}
                        aria-label="New Conversation"
                        title="New Conversation"
                        disabled={isLoading}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </Button>
                    <Button
                        className="btn-ghost btn-xs btn-circle"
                        onClick={() => setIsOpen(false)}
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </Button>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 text-sm bg-base-100 relative">
                {messages.map((msg) => (
                    <div key={msg.id} className={`chat ${msg.role === "ai" ? "chat-start" : "chat-end"}`}>
                        {msg.role === "ai" && (
                            <div className="chat-image avatar avatar-placeholder">
                                <div className="bg-primary text-primary-content text-xs rounded-full w-8 h-8 flex items-center justify-center shrink-0">
                                    <span>AI</span>
                                </div>
                            </div>
                        )}
                        <div className={`chat-bubble before:hidden ${msg.role === "ai" ? "chat-bubble-primary text-primary-content" : "bg-base-200 text-base-content"} text-sm`}>
                            {msg.image ? (
                                <div className="flex flex-col gap-2">
                                    <img
                                        src={`data:${msg.image.mimeType};base64,${msg.image.data}`}
                                        alt="AI generated image"
                                        className="max-w-full rounded-lg"
                                    />
                                    {blogId && (
                                        <button
                                            className="btn btn-xs btn-ghost w-fit self-end gap-1 opacity-80 hover:opacity-100"
                                            onClick={() => { setPendingImage(msg.image!); setImageName(""); setSaveStatus("idle"); }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                            </svg>
                                            Save as asset
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="prose prose-sm max-w-none prose-p:leading-relaxed prose-pre:bg-base-300/50 prose-pre:text-current text-current prose-headings:text-current prose-a:text-current prose-strong:text-current prose-code:text-current prose-li:text-current">
                                    {msg.text && <Markdown>{msg.text}</Markdown>}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="chat chat-start">
                        <div className="chat-image avatar avatar-placeholder">
                            <div className="bg-primary text-primary-content text-xs rounded-full w-8 h-8 flex items-center justify-center shrink-0">
                                <span>AI</span>
                            </div>
                        </div>
                        <div className="chat-bubble before:hidden chat-bubble-primary text-primary-content text-sm">
                            <span className="loading loading-dots loading-xs"></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-base-100 border-t border-base-300 shrink-0">
                <div className="flex items-end gap-1 max-w-4xl mx-auto w-full bg-base-200 rounded-3xl border border-transparent focus-within:border-primary/50 px-2 py-1">
                    <textarea
                        ref={inputRef}
                        rows={1}
                        placeholder="Ask AI..."
                        className="min-h-8 max-h-36 w-full flex-1 resize-none overflow-y-auto bg-transparent border-0 outline-none text-sm leading-5 py-1.5 px-2 disabled:opacity-50"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading || !conversationId}
                    />
                    <Button
                        className="btn-primary btn-sm btn-circle shrink-0 min-h-0 h-7 w-7 mb-0.5"
                        onClick={handleSend}
                        disabled={isLoading || !conversationId || !inputValue.trim()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
                        </svg>
                    </Button>
                </div>
            </div>

            {/* Save Image as Asset Modal */}
            {pendingImage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-base-100 rounded-2xl shadow-2xl border border-base-300 w-full max-w-md mx-4 overflow-hidden">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300 bg-base-200/60">
                            <h3 className="font-semibold text-sm">Save image as asset</h3>
                            <button
                                className="btn btn-ghost btn-xs btn-circle"
                                onClick={() => { setPendingImage(null); setSaveStatus("idle"); }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Image Preview */}
                        <div className="p-4 flex justify-center bg-base-200/40">
                            <img
                                src={`data:${pendingImage.mimeType};base64,${pendingImage.data}`}
                                alt="Preview"
                                className="max-h-64 rounded-lg object-contain shadow"
                            />
                        </div>

                        {/* Name Input + Actions */}
                        <div className="p-4 flex flex-col gap-3">
                            <input
                                type="text"
                                placeholder="Asset name (e.g. hero-photo)"
                                className="input input-sm w-full"
                                value={imageName}
                                onChange={e => setImageName(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") handleSaveImage(); }}
                                autoFocus
                                disabled={saveStatus === "saving" || saveStatus === "success"}
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => { setPendingImage(null); setSaveStatus("idle"); }}
                                    disabled={saveStatus === "saving"}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`btn btn-sm ${saveStatus === "success" ? "btn-success" : "btn-primary"}`}
                                    onClick={handleSaveImage}
                                    disabled={!imageName.trim() || saveStatus === "saving" || saveStatus === "success"}
                                >
                                    {saveStatus === "saving" && <span className="loading loading-spinner loading-xs" />}
                                    {saveStatus === "success" ? "Saved!" : saveStatus === "error" ? "Retry" : "Save"}
                                </button>
                            </div>
                            {saveStatus === "error" && (
                                <p className="text-error text-xs text-right">Upload failed — please try again.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
